import _ from "lodash";
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import ACLStore from "lib/aclStore";
import { ArrowRightPaneOutlined, CloseOutlined } from "@toddle-edu/ds-icons";
import { IconButton, useDSContext } from "@toddle-edu/ds-web";
import { markCommentRead } from "modules/Services";
import {
  createComment,
  updateComment,
  deleteComment,
} from "AppComponents/CommentBoxV2/modules/CommentThunks";
import { useI18n } from "Hooks";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import { graphql, compose } from "lib/apolloClient";
import { getCommentsOfNodeFromCache } from "modules/CommonGraphqlHelpers";
import { getCommentOfNodeQuery } from "modules/CommonQuery";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { prepareLoadMoreFeed } from "Utils";
import { getCommentsKey } from "./modules/Utils";

import classes from "./CommentBoxV2.scss";
import CommentFeed from "./components/CommentFeed";
import classNames from "classnames";

const CommentBox = props => {
  const {
    onClose,
    isData,
    isLoading,
    hasNextPage,
    comments,
    userId,
    fetchMoreFeed,
    getCommentOfNode,
    nodeId,
    collaborators,
    disableTagging,
    canCreateComment = true,
    parentType,
    isCommunity,
    canCreateAudioComment,
    mode,
    isRightPaneChat,
    placeholder,
    type,
    commentsKey,
    deleteComment,
    networkStatus,
    isCommentV2 = false,
    totalCounts,
    createComment,
    markCommentRead,
    updateComment,
    updateReadCount,
    testId,
    header,
    commentFilters,
    commentFeedContainerClass,
    noCommentContainerClass,
  } = props;

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";

  const { t } = useI18n(["common"]);

  const markCommentAsRead = () => {
    markCommentRead({
      nodeId,
      type,
      parentType,
      commentsKey,
      isCommunity,
      isCommentV2,
      updateReadCount,
      commentFilters,
    });
  };

  const updatedCollaborators = useMemo(() => {
    if (_.isEmpty(collaborators)) return [];
    return [
      {
        id: "all",
        firstName: t("common:all"),
        lastName: "",
        profileImage: "",
        iconType: "group",
        color: "neutral",
      },
      ..._.filter(
        _.map(_.get(collaborators, "edges"), item => {
          return item.node;
        }),
        item => item.id !== userId
      ),
    ];
  }, []);

  const createNewComment = ({ attachments, label } = {}) => {
    const entityName = _.get(getCommentOfNode, "node.title.value");
    createComment({
      nodeId,
      type,
      parentType,
      collaborators: updatedCollaborators,
      commentsKey,
      entityName,
      attachments,
      newCommentLabel: label,
      commentFilters,
    });
  };

  const handleClickDelete = ({ commentId }) => {
    const entityName = _.get(getCommentOfNode, "node.title.value");
    deleteComment({
      nodeId,
      comment_id: commentId,
      type,
      parentType,
      commentsKey,
      entityName,
      commentFilters,
    });
  };

  const editComment = async ({ id: commentId, label, attachments }) => {
    const commentDetails = _.get(
      _.find(comments, ({ node: { id } }) => {
        return commentId == id;
      }),
      "node",
      {}
    );
    await updateComment({
      comment_id: commentId,
      comment: _.trim(label),
      collaborators: updatedCollaborators,
      commentDetails,
      attachments,
      commentsKey,
    });
  };

  const headerContainerClasses = classNames({
    [classes.headerContainer]: true,
    [classes.headerContainerDSV2]: isDSV2,
  });

  const headerInnerContainerClasses = classNames({
    [classes.textPrimary]: isDSV2,
    "heading-5": !isDSV2,
  });

  return (
    <div className={classes.container}>
      {header || (
        <div className={headerContainerClasses}>
          <h6 className={headerInnerContainerClasses}>
            {t("common:comment_plural")}
          </h6>
          <IconButton
            _legacy_variant="plain"
            type="plain"
            variant="neutral"
            onClick={onClose}
            icon={
              isDSV2 ? (
                <ArrowRightPaneOutlined />
              ) : (
                <CloseOutlined size="xxx-small" />
              )
            }
            testId={`${testId}-close`}
          />
        </div>
      )}
      {networkStatus === 3 && (
        <div className={classes.loadingAnimationContainer}>
          <div className={classes.loadingAnimation} />
        </div>
      )}
      <CommentFeed
        isData={isData}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        comments={comments}
        userId={userId}
        fetchMoreFeed={fetchMoreFeed}
        nodeId={nodeId}
        collaborators={updatedCollaborators}
        disableTagging={disableTagging}
        canCreateAudioComment={canCreateAudioComment}
        mode={mode}
        t={t}
        isRightPaneChat={isRightPaneChat}
        handleClickDelete={handleClickDelete}
        placeholder={placeholder}
        parentType={parentType}
        editComment={editComment}
        createNewComment={createNewComment}
        canCreateComment={canCreateComment}
        networkStatus={networkStatus}
        totalCounts={totalCounts}
        markCommentAsRead={markCommentAsRead}
        testId={testId}
        commentFeedContainerClass={commentFeedContainerClass}
        noCommentContainerClass={noCommentContainerClass}
      />
    </div>
  );
};
const mapActionCreators = {
  createComment,
  deleteComment,
  updateComment,
  markCommentRead,
};

const mapStateToProps = (state, ownProps) => {
  const { commentsKey } = ownProps;
  const userInfo = getUserInfo();

  const canCreateAudioComment =
    _.get(ownProps, "canCreateAudioComment", true) &&
    ACLStore.can("Common:AddAudioComment");

  const isCommunity = state.login.activeTab == "community";

  return {
    userId: userInfo.id,
    isCommunity,
    commentsKey: getCommentsKey({ commentsKey, isCommunity }),
    canCreateAudioComment,
  };
};

const defaultProps = {
  deleteOtherComment: false,
  canCreateAudioComment: true,
  mode: "edit",
  disableTagging: false,
  isCommentV2: false,
  commentBoxContainerStyle: {},
  canCreateComment: true,
  collaborators: [],
  header: null,
};
const propTypes = {
  deleteOtherComment: PropTypes.bool,
  canCreateAudioComment: PropTypes.bool,
  mode: PropTypes.string,
  disableTagging: PropTypes.bool,
  isCommentV2: PropTypes.bool,
  commentBoxContainerStyle: PropTypes.object,
  canCreateComment: PropTypes.bool,
  collaborators: PropTypes.array,
  header: PropTypes.node,
};

CommentBox.defaultProps = defaultProps;
CommentBox.propTypes = propTypes;

const ComposedCommentBox = compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getCommentOfNodeQuery, {
    name: "getCommentOfNode",
    skip: ({ nodeId }) => !nodeId,
    options: ({ nodeId, type, firstCount, isCommunity, commentFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: nodeId,
        type: type,
        first: firstCount,
        isCommunity,
        commentFilters: commentFilters || undefined, // Used for projects module to filter comments based upon types
      },
    }),
    props({
      getCommentOfNode,
      ownProps: {
        nodeId,
        type,
        parentType,
        markCommentRead,
        isCommunity,
        commentsKey,
        commentFilters,
      },
    }) {
      const nodeFields = getCommentsOfNodeFromCache({
        nodeId: nodeId,
        type,
        isCommunity,
        commentFilters,
      });
      const comments = _.get(nodeFields, commentsKey, []);
      const endCursor = _.get(comments, "pageInfo.endCursor", null);
      return {
        getCommentOfNode,
        onPusherEventTriggered: params => {
          if (nodeId == params.parentId && parentType == params.parentType) {
            getCommentOfNode.refetch();
            markCommentRead({
              nodeId,
              type,
              parentType,
              isCommunity,
              commentsKey,
              commentFilters,
            });
          }
        },
        totalCounts: _.get(comments, "totalCount", 0),
        comments: _.reverse([..._.get(comments, "edges", [])]),
        hasNextPage: _.get(comments, "pageInfo.hasNextPage", false),
        isData: !_.isEmpty(nodeFields),
        isLoading:
          getCommentOfNode["networkStatus"] == 1 ||
          getCommentOfNode["networkStatus"] == 2,
        networkStatus: getCommentOfNode["networkStatus"],
        fetchMoreFeed: prepareLoadMoreFeed({
          variables: { after: endCursor, ...getCommentOfNode.variables },
          queryData: getCommentOfNode,
          key: commentsKey,
        }),
      };
    },
  }),
  withPusherBind(["nodeCommentFeedUpdate"])
)(CommentBox);

ComposedCommentBox.defaultProps = defaultProps;
ComposedCommentBox.propTypes = propTypes;

export default ComposedCommentBox;
